import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import { notify, confirmDialog } from "../../components/Notify";

export default function OpsPartners() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", contactEmail: "" });
  const [newKey, setNewKey] = useState(null); // { name, apiKey } shown after create/rotate
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [customers, setCustomers] = useState({});
  const [revealed, setRevealed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/ops/partners`, { headers });
      setPartners(data.partners || []);
    } catch (err) {
      notify(err.response?.data?.error || "Failed to load partners.", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPartner = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return notify("Partner name is required.", "error");
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/ops/partners`,
        { name: form.name.trim(), contactEmail: form.contactEmail.trim() || undefined },
        { headers });
      setNewKey({ name: data.partner?.name || form.name, apiKey: data.apiKey });
      setForm({ name: "", contactEmail: "" });
      notify("Partner created.");
      load();
    } catch (err) {
      notify(err.response?.data?.error || "Failed to create partner.", "error");
    } finally {
      setCreating(false);
    }
  };

  const rotateKey = async (p) => {
    const ok = await confirmDialog({
      title: `Rotate key for ${p.name}?`,
      message: "The current key stops working immediately. You'll get a new key to hand to the partner.",
      danger: true, okText: "Rotate key",
    });
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API_BASE_URL}/ops/partners/${p.id}/rotate-key`, {}, { headers });
      setNewKey({ name: p.name, apiKey: data.apiKey });
      notify("Key rotated.");
      load();
    } catch (err) {
      notify(err.response?.data?.error || "Failed to rotate key.", "error");
    }
  };

  const toggleCustomers = async (p) => {
    if (expanded === p.id) { setExpanded(null); return; }
    setExpanded(p.id);
    if (!customers[p.id]) {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/ops/partners/${p.id}/customers`, { headers });
        setCustomers((c) => ({ ...c, [p.id]: data.customers || [] }));
      } catch (err) {
        notify(err.response?.data?.error || "Failed to load customers.", "error");
      }
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    notify("Copied to clipboard.");
  };

  const badge = (status) => {
    const map = {
      active: { bg: "rgba(37,211,102,0.14)", c: "#7de8a8" },
      suspended: { bg: "rgba(245,181,68,0.16)", c: "#f5cd83" },
      cancelled: { bg: "rgba(239,90,90,0.16)", c: "#ff9d9d" },
    };
    const v = map[status] || { bg: "rgba(255,255,255,0.08)", c: "#aab0c4" };
    return <span style={{ background: v.bg, color: v.c, padding: "3px 10px", borderRadius: 100, fontSize: ".75rem", fontWeight: 600 }}>{status || "—"}</span>;
  };

  return (
    <div>
      {/* Create partner */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="mb-1">Create a partner</h6>
          <div className="small text-muted mb-3">
            A partner is a reseller that embeds Pairfon. They get an API key to provision customers server-to-server.
          </div>
          <form onSubmit={createPartner} className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small">Partner name</label>
              <input className="form-control" placeholder="e.g. Acme Reseller"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Contact email <span className="text-muted">(optional)</span></label>
              <input className="form-control" placeholder="ops@acme.com"
                value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
            <div className="col-md-3">
              <button className="btn btn-primary w-100" disabled={creating}>
                {creating ? "Creating…" : "Create partner"}
              </button>
            </div>
          </form>

          {newKey && (
            <div className="mt-3 p-3" style={{ background: "rgba(245,181,68,0.12)", borderRadius: 10, border: "1px solid rgba(245,181,68,0.35)" }}>
              <div className="small mb-1" style={{ color: "#f5cd83", fontWeight: 600 }}>
                <i className="bi bi-exclamation-triangle-fill me-1" />
                API key for “{newKey.name}” — shown once. Copy it now and give it to the partner.
              </div>
              <div className="d-flex align-items-center gap-2">
                <input readOnly value={newKey.apiKey} onFocus={(e) => e.target.select()}
                  className="form-control form-control-sm" style={{ fontFamily: "monospace" }} />
                <button type="button" className="btn btn-sm btn-outline-secondary" style={{ whiteSpace: "nowrap" }}
                  onClick={() => copy(newKey.apiKey)}>{copied ? "Copied" : "Copy"}</button>
                <button type="button" className="btn btn-sm btn-link text-muted" onClick={() => setNewKey(null)}>Dismiss</button>
              </div>
              <div className="small text-muted mt-2">
                The partner sends this as the <code>X-Partner-Key</code> header on <code>POST /api/partner/provision</code>.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Partner list */}
      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">Partners</h6>
          {loading ? (
            <p className="text-muted mb-0">Loading…</p>
          ) : partners.length === 0 ? (
            <p className="text-muted mb-0">No partners yet. Create one above.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Name</th><th>Contact</th><th>API key</th><th>Status</th><th>Customers</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <React.Fragment key={p.id}>
                      <tr>
                        <td className="fw-semibold">{p.name}</td>
                        <td className="text-muted">{p.contact_email || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {p.api_key ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <code style={{ fontSize: ".8rem" }}>
                                {revealed[p.id] ? p.api_key : "pk_••••••••••"}
                              </code>
                              <button className="btn btn-sm btn-link p-0" style={{ fontSize: ".78rem" }}
                                onClick={() => setRevealed((r) => ({ ...r, [p.id]: !r[p.id] }))}>
                                {revealed[p.id] ? "Hide" : "Show"}
                              </button>
                              <button className="btn btn-sm btn-link p-0" style={{ fontSize: ".78rem" }}
                                onClick={() => copy(p.api_key)}>Copy</button>
                            </span>
                          ) : (
                            <span className="text-muted small">Rotate key to view</span>
                          )}
                        </td>
                        <td>{badge(p.status)}</td>
                        <td>{p.customers ?? 0}</td>
                        <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                          <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => toggleCustomers(p)}>
                            {expanded === p.id ? "Hide" : "Customers"}
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => rotateKey(p)}>Rotate key</button>
                        </td>
                      </tr>
                      {expanded === p.id && (
                        <tr>
                          <td colSpan={6} style={{ background: "rgba(255,255,255,0.03)" }}>
                            {!customers[p.id] ? (
                              <span className="text-muted small">Loading customers…</span>
                            ) : customers[p.id].length === 0 ? (
                              <span className="text-muted small">No customers provisioned yet.</span>
                            ) : (
                              <table className="table table-sm mb-0" style={{ fontSize: ".85rem" }}>
                                <thead>
                                  <tr className="text-muted">
                                    <th>Company</th><th>Email</th><th>Ref</th><th>Plan</th><th>Billing</th><th>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customers[p.id].map((c) => (
                                    <tr key={c.company_id}>
                                      <td>{c.name}</td>
                                      <td className="text-muted">{c.email}</td>
                                      <td><code>{c.external_ref || "—"}</code></td>
                                      <td>{c.plan_code || "—"}</td>
                                      <td>{badge(c.billing_status)}</td>
                                      <td>{c.amount != null ? `${c.amount} ${(c.currency || "").toUpperCase()}` : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
